import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, Plus, Sparkles, Radio, MapPin, Trash2, AlertCircle, AlertTriangle, X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MonitoringPlansCard = ({ getAuthHeaders, isGuestMode, onGuestAction }) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extendingPlan, setExtendingPlan] = useState(null);

  useEffect(() => {
    if (isGuestMode) {
      setPlans([
        { id: 'demo-1', city: 'Wilton Manors', state: 'FL', category: 'Food & Drink', status: 'active', quarters_total: 1, start_date: '2026-03-01T00:00:00+00:00', end_date: '2026-06-01T00:00:00+00:00' },
      ]);
      setAlerts([
        { id: 'demo-alert-1', alert_type: 'red', title: 'Conflict Detected', description: 'Another event "Wilton Manors Stonewall Festival" is happening on one of your hold dates (Jun 15).', event_name: 'Stonewall Festival', created_at: new Date().toISOString() },
        { id: 'demo-alert-2', alert_type: 'orange', title: 'Day-Before Notice', description: 'Tomorrow is your event day — another event "Art Walk on the Drive" was just posted nearby.', event_name: 'Art Walk on the Drive', created_at: new Date(Date.now() - 86400000).toISOString() },
      ]);
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const [plansRes, alertsRes] = await Promise.all([
          axios.get(`${API}/discover/monitoring/plans`, { headers: getAuthHeaders() }),
          axios.get(`${API}/discover/monitoring/alerts`, { headers: getAuthHeaders() }),
        ]);
        setPlans(plansRes.data.filter(p => p.status === 'active' || p.status === 'paused'));
        setAlerts(alertsRes.data || []);
      } catch { /* silently fail */ }
      finally { setLoading(false); }
    };
    fetchData();
  }, [getAuthHeaders, isGuestMode]);

  const handleCancel = async (planId) => {
    if (isGuestMode) { onGuestAction(); return; }
    try {
      await axios.delete(`${API}/discover/monitoring/plans/${planId}`, { headers: getAuthHeaders() });
      setPlans(prev => prev.filter(p => p.id !== planId));
      toast.success('Monitoring plan cancelled');
    } catch {
      toast.error('Failed to cancel plan');
    }
  };

  const handleExtend = async (planId) => {
    if (isGuestMode) { onGuestAction(); return; }
    setExtendingPlan(planId);
    try {
      const res = await axios.post(`${API}/discover/monitoring/extend`,
        { plan_id: planId, additional_quarters: 1 },
        { headers: getAuthHeaders() }
      );
      setPlans(prev => prev.map(p => p.id === planId
        ? { ...p, quarters_total: res.data.new_quarters_total, end_date: res.data.new_end_date }
        : p
      ));
      toast.success(`Plan extended to ${res.data.new_quarters_total} quarters`, { duration: 6000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to extend plan');
    } finally {
      setExtendingPlan(null);
    }
  };

  const handleDismissAlert = async (alertId) => {
    if (isGuestMode) { onGuestAction(); return; }
    try {
      await axios.post(`${API}/discover/monitoring/alerts/${alertId}/dismiss`, {}, { headers: getAuthHeaders() });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch { /* silently fail */ }
  };

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); }
    catch { return '—'; }
  };

  const alertStyles = {
    red: 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30',
    orange: 'border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/30',
    activity: 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30',
  };

  const AlertIcon = ({ type }) => {
    if (type === 'red') return <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />;
    if (type === 'orange') return <AlertTriangle className="h-4 w-4 text-orange-600 flex-shrink-0" />;
    return <Sparkles className="h-4 w-4 text-blue-600 flex-shrink-0" />;
  };

  if (loading) return null;

  return (
    <Card className="hover:shadow-lg transition-all duration-300 border-2 hover:border-primary/30 group" data-testid="monitoring-plans-card">
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          <div className="p-2.5 bg-gradient-to-br from-violet-100 to-indigo-100 dark:from-violet-900/30 dark:to-indigo-900/30 rounded-xl group-hover:scale-110 transition-transform">
            <Radio className="h-5 w-5 text-violet-600 dark:text-violet-400" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-lg">Monitoring Plans</CardTitle>
            <CardDescription className="mt-1">
              Real-time event monitoring alerts
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {/* Active Alerts */}
        {alerts.length > 0 && (
          <div className="space-y-2" data-testid="monitoring-alerts-section">
            {alerts.slice(0, 3).map(alert => (
              <div
                key={alert.id}
                className={`p-2.5 rounded-lg border ${alertStyles[alert.alert_type] || alertStyles.activity} relative`}
                data-testid={`monitoring-alert-${alert.id}`}
              >
                <button
                  onClick={() => handleDismissAlert(alert.id)}
                  className="absolute top-1.5 right-1.5 text-muted-foreground hover:text-foreground"
                  data-testid={`dismiss-alert-${alert.id}`}
                >
                  <X className="h-3 w-3" />
                </button>
                <div className="flex items-start gap-2 pr-4">
                  <AlertIcon type={alert.alert_type} />
                  <div>
                    <p className="text-xs font-bold">{alert.title}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{alert.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {plans.length === 0 ? (
          <div className="space-y-3">
            {/* Mockup Example Card */}
            <div className="p-3 rounded-lg border border-dashed border-primary/20 bg-primary/5 opacity-60">
              <p className="text-[10px] text-primary font-medium mb-2 uppercase tracking-wider">Example Monitoring Plan</p>
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs font-medium">Wilton Manors, FL</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground ml-5">Food & Drink · Mar 2026 — Jun 2026 · 1Q</p>
                  <div className="ml-5 mt-1.5 space-y-1">
                    <p className="text-[10px] text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-2.5 w-2.5" /> Red: Conflict on your hold dates
                    </p>
                    <p className="text-[10px] text-orange-600 flex items-center gap-1">
                      <AlertTriangle className="h-2.5 w-2.5" /> Orange: Day-before event posted nearby
                    </p>
                    <p className="text-[10px] text-blue-600 flex items-center gap-1">
                      <Sparkles className="h-2.5 w-2.5" /> Activity: Weekly new event summary
                    </p>
                  </div>
                </div>
                <Badge variant="default" className="text-[10px]">Active</Badge>
              </div>
            </div>
            {/* Deep Search CTA */}
            <div className="p-3 rounded-lg border bg-muted/30 text-center">
              <p className="text-xs text-muted-foreground mb-2">Set up monitoring via Deep Search</p>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                onClick={() => isGuestMode ? onGuestAction() : navigate('/deep-search')}
                data-testid="monitoring-deep-search-btn"
              >
                <Sparkles className="h-3 w-3 mr-1" /> Deep Search
              </Button>
            </div>
          </div>
        ) : (
          plans.map(plan => (
            <div
              key={plan.id}
              className="p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
              data-testid={`monitoring-plan-${plan.id}`}
            >
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    <span className="text-sm font-medium truncate">{plan.city}{plan.state ? `, ${plan.state}` : ''}</span>
                  </div>
                  {plan.category && (
                    <p className="text-xs text-muted-foreground mt-0.5 ml-5.5">{plan.category}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-0.5 ml-5.5">
                    {formatDate(plan.start_date)} — {formatDate(plan.end_date)} · {plan.quarters_total}Q
                  </p>
                </div>
                <Badge variant={plan.status === 'active' ? 'default' : 'secondary'} className="text-xs flex-shrink-0">
                  {plan.status === 'active' ? 'Active' : 'Paused'}
                </Badge>
              </div>
              <div className="flex items-center gap-2 mt-2 justify-end">
                {plan.quarters_total < 4 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => handleExtend(plan.id)}
                    disabled={extendingPlan === plan.id}
                    data-testid={`extend-plan-${plan.id}`}
                  >
                    {extendingPlan === plan.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                    Renew +1Q (3 credits)
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground hover:text-destructive"
                  onClick={() => handleCancel(plan.id)}
                  data-testid={`cancel-plan-${plan.id}`}
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Cancel
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default MonitoringPlansCard;
